'use client';

import Link from 'next/link';
import { JSX } from 'react';

import { Text } from '@videoblocks/design-system';

import {
    AI_TOOLKIT_CATEGORIES,
    AI_TOOLKIT_OPERATIONS,
    AiToolkitCategory,
    AiToolkitOperation,
} from '../../_context/types';

export interface ToastTitleProps {
    category: AiToolkitCategory;
    status: 'inProgress' | 'success' | 'error';
    href: string;
    operation?: AiToolkitOperation;
}

const categoryToTitle = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: 'video',
    [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]: 'image',
    [AI_TOOLKIT_CATEGORIES.VOICEOVER]: 'voiceover',
    [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]: 'video',
};

/** Video edit is the only tool with an upscale operation today. */
const UPSCALE_NOUN: Partial<Record<AiToolkitCategory, string>> = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]: 'upscaled video',
};

const categoryToToolkitName = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: 'Video Generator',
    [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]: 'Image Generator',
    [AI_TOOLKIT_CATEGORIES.VOICEOVER]: 'Voiceover Generator',
    [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]: 'Video Editor',
};

export const ToastTitle = ({
    category = AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION,
    status = 'inProgress',
    href = '/ai/video-generation',
    operation = AI_TOOLKIT_OPERATIONS.GENERATE,
}: ToastTitleProps): JSX.Element => {
    /** What the finished artifact is called - the same thing whichever operation produced it. */
    const resultNoun = categoryToTitle[category];

    const operationNoun =
        (operation === AI_TOOLKIT_OPERATIONS.UPSCALE ? UPSCALE_NOUN[category] : undefined) ?? resultNoun;

    const inProgressText = (
        <Text fontWeight="regular">
            We&apos;re generating your {operationNoun}, click&nbsp;
            <Text asChild fontWeight="bold" className="underline">
                <Link href={href}>here</Link>
            </Text>
            &nbsp;to check the status. This may take a few minutes &ndash; we&apos;ll notify you when it&apos;s ready.
        </Text>
    );

    const successText = (
        <Text fontWeight="regular">
            Your {resultNoun} is ready! Click&nbsp;
            <Text asChild fontWeight="bold" className="underline">
                <Link href={href}>here</Link>
            </Text>
            &nbsp;to view it.
        </Text>
    );

    const errorText = (
        <Text fontWeight="regular">
            Something went wrong while generating your {operationNoun}. Please check&nbsp;
            <Text asChild fontWeight="bold" className="underline">
                <Link href={href}>{categoryToToolkitName[category]}</Link>
            </Text>
            &nbsp;and try again.
        </Text>
    );

    return status === 'inProgress' ? inProgressText : status === 'success' ? successText : errorText;
};
